import { useState } from "react";
import { toast } from "sonner";
import { Phone, MapPin, Send, Check } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { COMPANY, SERVICES } from "@/data/content";
import { Section, Overline, usePageTitle } from "@/components/site/ui-bits";

const empty = { name: "", email: "", phone: "", client_type: "particulier", service: SERVICES[0].title, address: "", city: "", message: "" };

export default function Contact() {
  usePageTitle("Contact & devis gratuit");
  const [form, setForm] = useState(empty);
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/quotes", form);
      setSent(true);
      toast.success("Demande envoyée ! Nous vous recontactons sous 48h.");
      setForm(empty);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-[#0ea5e9] focus:ring-2 focus:ring-sky-100 outline-none transition text-sm";

  return (
    <div data-testid="contact-page">
      <section className="bg-gradient-to-b from-sky-50 to-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-24 text-center">
          <Overline>Contact</Overline>
          <h1 className="mt-4 text-4xl sm:text-5xl font-bold tracking-tight text-slate-900">Demandez votre devis gratuit</h1>
          <p className="mt-5 text-slate-600 max-w-2xl mx-auto">Remplissez le formulaire ci-dessous, nous vous répondons sous 48h.</p>
        </div>
      </section>

      <Section>
        <div className="grid lg:grid-cols-5 gap-10">
          <div className="lg:col-span-3 bg-white rounded-3xl border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-8">
            {sent ? (
              <div data-testid="quote-success" className="text-center py-16">
                <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto"><Check size={30} /></div>
                <h2 className="mt-5 text-2xl font-bold text-slate-900">Merci !</h2>
                <p className="mt-2 text-slate-600">Votre demande a bien été envoyée. Nous vous recontactons rapidement.</p>
                <button onClick={() => setSent(false)} className="mt-6 text-[#0ea5e9] font-semibold">Envoyer une autre demande</button>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="quote-form" className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="text-sm font-medium text-slate-700">Nom complet *</label>
                    <input data-testid="quote-name" required value={form.name} onChange={set("name")} className={`mt-1.5 ${inputCls}`} placeholder="Jean Dupont" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700">Téléphone *</label>
                    <input data-testid="quote-phone" required value={form.phone} onChange={set("phone")} className={`mt-1.5 ${inputCls}`} placeholder="06 12 34 56 78" />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Email *</label>
                  <input data-testid="quote-email" type="email" required value={form.email} onChange={set("email")} className={`mt-1.5 ${inputCls}`} placeholder="jean@email.fr" />
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="text-sm font-medium text-slate-700">Vous êtes</label>
                    <select data-testid="quote-client-type" value={form.client_type} onChange={set("client_type")} className={`mt-1.5 ${inputCls}`}>
                      <option value="particulier">Particulier</option>
                      <option value="professionnel">Professionnel</option>
                      <option value="collectivité">Collectivité</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700">Prestation souhaitée</label>
                    <select data-testid="quote-service" value={form.service} onChange={set("service")} className={`mt-1.5 ${inputCls}`}>
                      {SERVICES.map((s) => <option key={s.slug} value={s.title}>{s.title}</option>)}
                      <option value="Autre">Autre</option>
                    </select>
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="text-sm font-medium text-slate-700">Ville</label>
                    <input data-testid="quote-city" value={form.city} onChange={set("city")} className={`mt-1.5 ${inputCls}`} placeholder="Cahors" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700">Adresse</label>
                    <input data-testid="quote-address" value={form.address} onChange={set("address")} className={`mt-1.5 ${inputCls}`} placeholder="12 rue ..." />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700">Votre message</label>
                  <textarea data-testid="quote-message" rows={4} value={form.message} onChange={set("message")} className={`mt-1.5 ${inputCls}`} placeholder="Décrivez votre besoin..." />
                </div>
                <button data-testid="quote-submit" disabled={loading} className="w-full bg-[#0ea5e9] hover:bg-[#0284c7] disabled:opacity-60 text-white px-6 py-3.5 rounded-full font-bold transition-colors inline-flex items-center justify-center gap-2">
                  {loading ? "Envoi..." : <>Envoyer ma demande <Send size={17} /></>}
                </button>
              </form>
            )}
          </div>

          <div className="lg:col-span-2 space-y-5">
            <div className="bg-[#0ea5e9] text-white rounded-3xl p-8">
              <h3 className="text-xl font-bold">Contact direct</h3>
              <ul className="mt-5 space-y-4 text-sm">
                <li className="flex items-center gap-3"><Phone size={18} /> <a href={`tel:${COMPANY.phoneRaw}`} className="hover:underline">{COMPANY.phone}</a></li>
                {COMPANY.addresses.map((a) => <li key={a} className="flex items-center gap-3"><MapPin size={18} /> {a}</li>)}
              </ul>
              <a href={`https://wa.me/${COMPANY.whatsapp}`} target="_blank" rel="noreferrer" className="mt-6 block text-center bg-white text-[#0284c7] px-6 py-3 rounded-full font-bold hover:bg-sky-50 transition-colors">WhatsApp 24h/24</a>
            </div>
            <div className="bg-sky-50 border border-sky-100 rounded-3xl p-6">
              <h3 className="font-semibold text-slate-900">💙 50 % de crédit d'impôt</h3>
              <p className="mt-2 text-sm text-slate-600">Organisme <b>agréé Services à la Personne</b>. Avec l'<b>Avance Immédiate</b>, les particuliers ne paient que <b>la moitié</b> de la facture pour les prestations à domicile.</p>
              <p className="mt-2 text-sm text-slate-600">Renseignez-vous auprès de Gabriel (bulle de chat en bas à droite) ou auprès d'un de nos conseillers en remplissant le formulaire ci-contre ou en nous appelant au <b>05 32 18 90 44</b>.</p>
            </div>
            <div className="bg-slate-50 rounded-3xl p-8 border border-slate-100">
              <h3 className="font-semibold text-slate-900">Horaires</h3>
              <p className="mt-2 text-sm text-slate-600">Du lundi au samedi : 7h – 22h<br />Joignables sur WhatsApp 24h/24, 7j/7.</p>
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
}
